import React, { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { useTheme } from '@mui/material/styles'
import { Button, MenuItem, useMediaQuery } from '@mui/material'
import { Actions, DownshiftProps, PropGetters } from 'downshift'
import { ProductSearchType, SearchFocusType } from '@utils/searchUtils'
import { Close } from '@mui/icons-material'
import {
  GetIndex,
  SearchOptions,
  StartingIndexes,
  Suggestions,
} from './useHeaderAutoSuggest'
import { headerSearchAddToCartStorage } from './useHeaderSearchAddToCart'
import HeaderSearchLinks from './HeaderSearchLinks'
import messages from '@utils/messages'
import HandleMarkup from '@src/components/HandleMarkup'
import slugify from '@utils/slugify'
import escapeStringRegexp from 'escape-string-regexp'
import useTrueVH from '@src/styles/utils/useTrueVH'
import { HeaderSearchAddToCartProps } from './HeaderSearchAddToCart'
import { usePersistentStorage } from '@src/utils/usePersistentStorage'
import { Suggestion } from '@src/types/graphql-types'
import { RecentOrderItemFragment } from '@src/queries/RecentOrderItemsQuery.generated'
import {
  sendHeaderSearchEvent,
  sendSearchAnalyticsEvent,
} from '@utils/analytics'
import {
  AddToCartPagesEnum,
  EventActionComponentType,
} from '@utils/analytics/enums'
import { Link } from '@src/components/Link'
import { orderCenterRoutes } from '@src/routes'
import { sendPricingAvailabilityInteractionEvent } from '@src/utils/analytics/pricingAndAvailability'
import ProductQuickLook from '@src/components/ProductQuickLook'

const useStyles = makeStyles()((theme) => ({
  autosuggestMenu: {
    position: 'absolute',
    width: '100%',
    zIndex: theme.zIndex.modal,
    overflowY: 'auto',
    backgroundColor: theme.palette.common.white,
    paddingTop: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      paddingTop: 0,
      top: 52,
      width: '100%',
      height: 'auto !important', // Important to overrule inline styling needed for mobile sizes
      boxShadow: '0px 6px 13px 0px rgba(0, 0, 0, 0.16)',
    },
    [theme.breakpoints.up('lg')]: {
      top: 54,
    },
  },
  sectionsWrapper: {
    overflow: 'hidden',
    '& > *': {
      marginTop: -1,
    },
  },
  alertWrapper: {
    padding: theme.spacing(2, 4, 1),
    display: 'none',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
    },
  },
  alertMessage: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  suggestionsWrapper: {
    padding: theme.spacing(1, 0, 2),
    '&:empty': { display: 'none' },
  },
  categoryTitle: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    margin: theme.spacing(2, 0, 0.5, 3),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  suggestion: {
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(0.75, 5),
  },
  truncate: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  quickViewMenuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    '& > button': {
      display: 'none',
    },
    '&:hover': {
      '& > button': {
        [theme.breakpoints.up('md')]: {
          display: 'block',
        },
      },
    },
    '&.Mui-selected': {
      '& > button': {
        display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'block',
        },
      },
    },
  },
  menuItemFocused: {
    '& > button': {
      display: 'block',
    },
    height: '500px',
  },
  quickViewButton: {
    display: 'none',
    fontSize: theme.typography.pxToRem(12),
    padding: 0,
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  noQuickViewItem: {
    height: '300px',
  },
  newFeatureMessage: {
    display: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    [theme.breakpoints.up('md')]: {
      display: 'inline',
    },
  },
  seeAll: {
    marginLeft: theme.spacing(1),
  },
  closeIcon: {
    position: 'absolute',
    right: theme.spacing(8),
    top: theme.spacing(8),
    minWidth: 'auto',
    padding: 0,
    color: theme.palette.common.black,
    '&:hover': {
      color: theme.palette.common.black,
    },
    '& svg': {
      fontSize: theme.typography.pxToRem(28),
    },
  },
}))

interface HeaderSearchMenuProps {
  isOpen: DownshiftProps<SearchOptions>['isOpen']
  getMenuProps: PropGetters<SearchOptions>['getMenuProps']
  getItemProps: PropGetters<SearchOptions>['getItemProps']
  highlightedIndex: DownshiftProps<SearchOptions>['highlightedIndex']
  suggestions: Suggestions
  suggestionsCount: number
  recentSearches: string[]
  recentPurchases: RecentOrderItemFragment[]
  searchTerm: string
  getItemIndex: GetIndex
  minTermLength: number
  focusType: SearchFocusType
  orgId: string | null | undefined
  headerSearchAddToCartProps: HeaderSearchAddToCartProps
  setProdNumberQuickLook: (product: Suggestion | null) => void
  prodNumberQuickLook: Suggestion | null
  closeQuickLook: () => void
  closeMenu: Actions<SearchOptions>['closeMenu']
  toggleMarketplaceSellerModalOpen?: () => void
}

const HeaderSearchMenu: React.FC<HeaderSearchMenuProps> = ({
  isOpen,
  getMenuProps,
  getItemProps,
  suggestions,
  highlightedIndex,
  searchTerm,
  getItemIndex,
  suggestionsCount,
  recentSearches,
  recentPurchases,
  minTermLength,
  focusType,
  orgId,
  headerSearchAddToCartProps,
  setProdNumberQuickLook,
  prodNumberQuickLook,
  closeQuickLook,
  closeMenu,
  // renderInternal contributes a large amount to the cognitive-complexity.
  // Refactoring would satisfy the lint rule but possibly make the code harder to read.
}) => {
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const { headerSearchAddToCartUsed } =
    usePersistentStorage(headerSearchAddToCartStorage) || {}
  const productNameSuggestions = suggestions.productName
  const productNumberSuggestions = suggestions.productNumber
  const casNumberSuggestions = suggestions.casNumber
  const showSuggestions = !!(
    suggestionsCount && searchTerm.length >= minTermLength
  )

  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const highlightText = (searchTerm: string, text: string) => {
    const regExp = new RegExp(escapeStringRegexp(searchTerm), 'gi')
    return text.replace(regExp, (match: string) => `<b>${match}</b>`)
  }
  const viewportHeight = useTrueVH()
  const prodNumberQuickLookRef = useRef(
    headerSearchAddToCartProps.material?.number || null
  )

  useEffect(() => {
    setProdNumberQuickLook(null)
    prodNumberQuickLookRef.current = null

    const materialNumberMatch =
      headerSearchAddToCartProps.material?.number.toLowerCase()

    if (
      materialNumberMatch &&
      materialNumberMatch !== prodNumberQuickLookRef.current
    ) {
      prodNumberQuickLookRef.current = materialNumberMatch
    }
  }, [searchTerm, headerSearchAddToCartProps.material])

  useEffect(() => {
    if (!isOpen) {
      setProdNumberQuickLook(null)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      closeMenu()
    }
  }, [closeMenu])

  if (!isOpen) return null
  const getID = (
    item: string,
    context: 'category' | 'product_name' | 'product_number' | 'CAS'
  ) => `header-search-result-${context}_${slugify(item)}`

  const { material, product } = headerSearchAddToCartProps

  const quickViewItem = material || product

  const quickViewImage = {
    mediumUrl:
      material?.image?.mediumUrl || product?.images?.[0]?.mediumUrl || '',
  }

  const quickViewProductKey = material?.product || product?.productKey || ''

  const quickViewProductNumber =
    material?.number || product?.productNumber || ''

  return (
    <div
      {...getMenuProps()}
      className={classes.autosuggestMenu}
      style={{ height: viewportHeight - 84 }}
      data-testid="search-links"
    >
      <div className={classes.sectionsWrapper}>
        {/* Dynamic Suggestions Per Category */}
        {(function renderInternal() {
          if (
            prodNumberQuickLook &&
            headerSearchAddToCartProps.product?.productKey !==
              prodNumberQuickLook.productKey &&
            !quickViewItem &&
            !quickViewProductNumber
          )
            return <div className={classes.noQuickViewItem} />
          if (focusType !== SearchFocusType.Products && !orgId) return null
          if (!showSuggestions && !prodNumberQuickLook?.productKey) return null
          if (
            (headerSearchAddToCartProps.material ||
              prodNumberQuickLook?.productKey) &&
            isDesktop
          ) {
            return (
              <>
                <Button
                  aria-label="close"
                  className={classes.closeIcon}
                  onClick={() => {
                    closeQuickLook()
                    setProdNumberQuickLook(null)
                    sendPricingAvailabilityInteractionEvent({
                      action: 'close p&a',
                      section: 'quick view p&a',
                      component: 'modal',
                      elementType: 'icon',
                      gaType: AddToCartPagesEnum.HeaderSearch,
                      material: {
                        materialDescription: headerSearchAddToCartProps.product
                          ?.substance.key
                          ? headerSearchAddToCartProps.product?.substance.key
                          : undefined,
                        brand: headerSearchAddToCartProps.product?.brand.key,
                        product:
                          headerSearchAddToCartProps.product?.productNumber,
                      },
                    })
                  }}
                >
                  <Close />
                </Button>
                <ProductQuickLook
                  open
                  onClose={() => {
                    setProdNumberQuickLook(null)
                    closeQuickLook()
                  }}
                  renderInModal={false}
                  renderDocuments
                  image={quickViewImage}
                  empiricalFormula={quickViewItem?.empiricalFormula}
                  linearFormula={quickViewItem?.linearFormula}
                  casNumber={quickViewItem?.casNumber}
                  molecularWeight={quickViewItem?.molecularWeight}
                  brand={
                    quickViewItem?.brand ?? {
                      color: '',
                      key: '',
                      name: '',
                    }
                  }
                  productKey={quickViewProductKey}
                  productNumber={quickViewProductNumber}
                  name={quickViewItem?.name || ''}
                  description={quickViewItem?.description}
                  initialMaterial={quickViewProductNumber}
                  isMarketplace={!!quickViewItem?.isMarketplace}
                  marketplaceSellerId={
                    quickViewItem?.marketplaceSellerId || undefined
                  }
                  erpType={
                    (headerSearchAddToCartProps.product
                      ?.erp_type as string[]) || []
                  }
                  gaType={AddToCartPagesEnum.HeaderSearch}
                  gaPricingOverrides={{
                    section: 'header search quick view',
                    component: EventActionComponentType.Modal,
                  }}
                />
              </>
            )
          }

          return (
            <div className={classes.suggestionsWrapper}>
              {/* Display Product Name Suggestions if they Exist */}
              {productNameSuggestions.length ? (
                <>
                  <div className={classes.categoryTitle}>
                    <FormattedMessage {...messages.PRODUCT_NAME} />
                  </div>
                  {productNameSuggestions.map((item, index) => (
                    <MenuItem
                      key={item.label}
                      {...getItemProps({
                        key: item.label,
                        item: {
                          item: item.label,
                          type: ProductSearchType.Product,
                          searchType: ProductSearchType.ProductName,
                        },
                        index: getItemIndex(
                          StartingIndexes.PRODUCT_NAME,
                          index,
                          showSuggestions
                        ),
                        selected:
                          getItemIndex(
                            StartingIndexes.PRODUCT_NAME,
                            index,
                            showSuggestions
                          ) === highlightedIndex,
                        className: classes.suggestion,
                        id: getID(item.label, 'product_name'),
                      })}
                    >
                      <HandleMarkup
                        className={classes.truncate}
                        value={highlightText(searchTerm, item.label)}
                      />
                    </MenuItem>
                  ))}
                </>
              ) : null}
              {/* Display Product Number Suggestions if they Exist */}
              {productNumberSuggestions.length ? (
                <>
                  <div className={classes.categoryTitle}>
                    <FormattedMessage {...messages.PRODUCT_NUMBER} />
                    <span
                      className={classes.newFeatureMessage}
                    >{` (${formatMessage(
                      messages.NEW_FEATURE_QUICK_VIEW
                    )})`}</span>
                  </div>
                  {productNumberSuggestions.map((item, index) => (
                    <MenuItem
                      key={item.label}
                      {...getItemProps({
                        key: item.label,
                        item: {
                          item: item.label,
                          type: ProductSearchType.ProductNumber,
                          searchType: ProductSearchType.ProductNumber,
                        },
                        index: getItemIndex(
                          StartingIndexes.PRODUCT_NUMBER,
                          index,
                          showSuggestions
                        ),
                        selected:
                          getItemIndex(
                            StartingIndexes.PRODUCT_NUMBER,
                            index,
                            showSuggestions
                          ) === highlightedIndex,
                        className: cx(
                          classes.suggestion,
                          classes.quickViewMenuItem
                        ),
                        id: getID(item.label, 'product_number'),
                      })}
                    >
                      <HandleMarkup
                        className={classes.truncate}
                        value={highlightText(searchTerm, item.label)}
                      />
                      <Button
                        className={classes.quickViewButton}
                        onClick={(e) => {
                          e.stopPropagation()
                          sendHeaderSearchEvent()
                          sendPricingAvailabilityInteractionEvent({
                            action: 'view p&a',
                            section: 'header search',
                            component: 'autosuggest',
                            elementText: 'quick view',
                            elementType: 'link',
                            material: {
                              materialDescription:
                                item.substanceKey || undefined,
                              brand: item.brandKey || undefined,
                              product: item.productKey || undefined,
                            },
                          })
                          setProdNumberQuickLook(item)
                        }}
                      >
                        <FormattedMessage {...messages.QUICK_VIEW} />
                      </Button>
                    </MenuItem>
                  ))}
                </>
              ) : null}
              {/* Display CAS Suggestions if they Exist */}
              {casNumberSuggestions.length ? (
                <>
                  <div className={classes.categoryTitle}>
                    <FormattedMessage {...messages.CAS_NUMBER} />
                  </div>
                  {casNumberSuggestions.map((item, index) => (
                    <MenuItem
                      key={item.label}
                      {...getItemProps({
                        key: item.label,
                        item: {
                          item: item.label,
                          type: ProductSearchType.Product,
                          searchType: ProductSearchType.CasNumber,
                        },
                        index: getItemIndex(
                          StartingIndexes.CAS_NUMBER,
                          index,
                          showSuggestions
                        ),
                        selected:
                          getItemIndex(
                            StartingIndexes.CAS_NUMBER,
                            index,
                            showSuggestions
                          ) === highlightedIndex,
                        className: classes.suggestion,
                        id: getID(item.label, 'CAS'),
                      })}
                    >
                      <HandleMarkup
                        className={classes.truncate}
                        value={highlightText(searchTerm, item.label)}
                      />
                    </MenuItem>
                  ))}
                </>
              ) : null}
            </div>
          )
        })()}

        {!showSuggestions && !prodNumberQuickLook?.productKey && (
          <div className={classes.suggestionsWrapper}>
            {focusType === SearchFocusType.Products &&
              !headerSearchAddToCartUsed && (
                <div className={classes.alertWrapper}>
                  <div className={classes.alertMessage}>
                    <FormattedMessage {...messages.ADD_TO_CART_TIP} />
                  </div>
                </div>
              )}
            {recentSearches.length > 0 && (
              <>
                <div className={classes.categoryTitle}>
                  <FormattedMessage {...messages.RECENT_SEARCHES} />
                </div>
                {recentSearches.map((recentSearch, index) => (
                  <MenuItem
                    key={recentSearch}
                    {...getItemProps({
                      key: recentSearch,
                      index: getItemIndex(
                        StartingIndexes.RECENT_SEARCHES,
                        index,
                        showSuggestions
                      ),
                      item: { item: recentSearch, recentSearch: true },
                      selected:
                        getItemIndex(
                          StartingIndexes.RECENT_SEARCHES,
                          index,
                          showSuggestions
                        ) === highlightedIndex,
                      className: classes.suggestion,
                    })}
                  >
                    {recentSearch}
                  </MenuItem>
                ))}
              </>
            )}
            {recentPurchases.length > 0 &&
              focusType === SearchFocusType.Products && (
                <>
                  <div className={classes.categoryTitle}>
                    <FormattedMessage {...messages.RECENT_PURCHASES} />
                    <Link href={orderCenterRoutes.buyItAgain()}>
                      {/* jsx-a11y rules disabled because href is implicitly passed to anchor */}
                      {}
                      <a
                        onClick={() => {
                          closeMenu()
                          sendHeaderSearchEvent()
                        }}
                        className={classes.seeAll}
                      >
                        <FormattedMessage {...messages.SEE_ALL} />
                      </a>
                    </Link>
                  </div>
                  {recentPurchases.map((recentOrderItem, index) => (
                    <MenuItem
                      key={recentOrderItem.orderItemId}
                      {...getItemProps({
                        key: recentOrderItem.orderItemId,
                        index: getItemIndex(
                          StartingIndexes.RECENT_PURCHASES,
                          index,
                          showSuggestions
                        ),
                        item: {
                          item: recentOrderItem.material.number,
                        },
                        selected:
                          getItemIndex(
                            StartingIndexes.RECENT_PURCHASES,
                            index,
                            showSuggestions
                          ) === highlightedIndex,
                        className: cx(
                          classes.suggestion,
                          classes.quickViewMenuItem
                        ),
                        onClick: () => {
                          sendSearchAnalyticsEvent(
                            {
                              searchType: 'recent purchases',
                              href: undefined,
                              item: recentOrderItem.material.number,
                              searchTerm,
                              facetValue: undefined,
                            },
                            focusType
                          )
                        },
                      })}
                    >
                      <span> {recentOrderItem.material.number}</span>
                      <Button
                        className={classes.quickViewButton}
                        onClick={(e) => {
                          e.stopPropagation()
                          sendHeaderSearchEvent()
                          setProdNumberQuickLook({
                            label: recentOrderItem.material.product,
                            brandKey: recentOrderItem.material.brand.key,
                            productKey: recentOrderItem.material.product,
                          })
                        }}
                      >
                        <FormattedMessage {...messages.QUICK_VIEW} />
                      </Button>
                    </MenuItem>
                  ))}
                </>
              )}
          </div>
        )}
        {/* Static Search Suggestions */}
        {!headerSearchAddToCartProps.material && !prodNumberQuickLook && (
          <HeaderSearchLinks
            getItemIndex={getItemIndex}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            showSuggestions={showSuggestions}
          />
        )}
      </div>
    </div>
  )
}

export default HeaderSearchMenu
